import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  Package, 
  Settings, 
  ChevronDown,
  DollarSign,
  X,
  ChevronLeft,
  ChevronRight,
  Factory,
  BarChart3,
  Boxes,
  LogIn,
  LogOut,
  ArrowLeftRight,
  Receipt,
  Settings2,
  PaintBucket,
  Grid3x3,
  Printer,
  Tags,
  Users,
  Book,
  Repeat2,
  CreditCard,
  FileText,
  FileSignature,
  TrendingUp,
  Wallet,
  PackageX,
  BarChart2,
  Calculator,
  UserCog,
  Scale,
  Target,
  ListChecks,
  Shield,
  Database,
  Gauge,
  FolderKanban,
  Bell,
  ShoppingCart,
  Banknote
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { syncPermissions, getMyAbilities } from '../services/rbacService';
import { pathToPermissionKey } from '../utils/permissions';

const Sidebar = ({ open, setOpen }) => {
  const location = useLocation();
  const { user } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [brandingName, setBrandingName] = useState('ERP');
  const [brandingLogoUrl, setBrandingLogoUrl] = useState('/api/company/logo');
  const [expandedMenus, setExpandedMenus] = useState({
    inventory: false,
    customer: false,
    production: false,
    finance: false,
    reports: false,
    management: false,
    settings: false
  });

  const navigation = useMemo(() => [
    { 
      name: 'Dashboard', 
      href: '/', 
      icon: Gauge,
      type: 'single'
    },
    { 
      name: 'Stok Yönetimi', 
      icon: Package,
      type: 'expandable',
      key: 'inventory',
      children: [
        { name: 'Stok Kartları', href: '/stock-cards', icon: Package },
        { name: 'Stok Giriş', href: '/stock-entry', icon: LogIn },
        { name: 'Stok Çıkış', href: '/stock-exit', icon: LogOut },
        { name: 'Stok Envanter', href: '/stock-inventory', icon: Boxes },
        { name: 'Stok Hareketleri', href: '/stock-movements', icon: ArrowLeftRight },
        { name: 'Stok Maliyetleri', href: '/stock-costs', icon: Receipt },
        { name: 'Stok Düzenlemeleri', href: '/stock-adjustments', icon: Settings2 }
      ]
    },
    { 
      name: 'Müşteri Yönetimi', 
      icon: Users,
      type: 'expandable',
      key: 'customer',
      children: [
        { name: 'Müşteri Siparişleri', href: '/customer-management/orders', icon: ShoppingCart },
        { name: 'Müşteri Ön Siparişleri', href: '/customer-management/pre-orders', icon: FileText },
        { name: 'İlk Satışlar', href: '/customer-management/first-sales', icon: TrendingUp }
      ]
    },
    { 
      name: 'Üretim Takip', 
      icon: Factory,
      type: 'expandable',
      key: 'production',
      children: [
        { name: 'Boyahane Yönetimi', href: '/uretim-takip/boyahane-yonetimi', icon: PaintBucket },
        { name: 'Örme Yönetimi', href: '/uretim-takip/orme-yonetimi', icon: Grid3x3 },
        { name: 'Baskı Yönetimi', href: '/uretim-takip/baski-yonetimi', icon: Printer },
        { name: 'İşletme Tanımları', href: '/uretim-takip/isletme-tanimlari', icon: Tags }
      ]
    },
    { 
      name: 'Finans Yönetimi', 
      icon: DollarSign,
      type: 'expandable',
      key: 'finance',
      children: [
        { name: 'Cari Kartlar', href: '/current-accounts', icon: Users },
        { name: 'Cari Hesaplar', href: '/finance/current-balances', icon: Book },
        { name: 'Cari Hesap Hareketleri', href: '/finance/current-ledger', icon: Repeat2 },
        { name: 'Ödeme ve Tahsilat', href: '/finance/payments', icon: CreditCard },
        { name: 'Cari Dekont Fişleri', href: '/finance/dekont', icon: FileText },
        { name: 'Çek/Senet Durumu', href: '/finance/check-notes', icon: FileSignature },
        { name: 'Finans Hareketleri', href: '/finance/movements', icon: TrendingUp },
        { name: 'Gider Yönetimi', href: '/finance/expenses', icon: Wallet }
      ]
    },
    { 
      name: 'Analiz ve Raporlar', 
      icon: BarChart3,
      type: 'expandable',
      key: 'reports',
      children: [
        { name: 'Satış Raporları', href: '/reports/sales', icon: BarChart2 },
        { name: 'Kar Analizi', href: '/reports/profit', icon: Calculator },
        { name: 'Atıl Stoklar', href: '/reports/idle-stocks', icon: PackageX },
        { name: 'Tahsilat ve Ödeme Raporları', href: '/reports/payment-collection', icon: DollarSign },
        { name: 'Çek ve kredi kartı hareketleri', href: '/reports/check-credit-activity', icon: Banknote },
        { name: 'Stok Planlama', href: '/reports/stock-planning', icon: BarChart3 }
      ]
    },
    { 
      name: 'Yönetimsel Raporlar', 
      icon: FolderKanban,
      type: 'expandable',
      key: 'management',
      children: [
        { name: 'Personel', href: '/management/personnel', adminOnly: true, icon: UserCog },
        { name: 'Bilanço', href: '/management/balance-sheet', adminOnly: true, icon: Scale },
        { name: 'Hedefler', href: '/management/targets', adminOnly: true, icon: Target }
      ]
    },
    { 
      name: 'Ayarlar', 
      icon: Settings,
      type: 'expandable',
      key: 'settings',
      children: [
        { name: 'Tanımlamalar', href: '/definitions', icon: ListChecks },
        { name: 'Kullanıcı Yönetimi', href: '/users', adminOnly: true, icon: Shield },
        { name: 'Database Erişimi', href: '/settings/database', adminOnly: true, icon: Database },
        { name: 'Bildirim Ayarları', href: '/settings/notifications', adminOnly: true, icon: Bell }
      ]
    },
  ], []);

  // Build permission defs from navigation
  const permissionDefs = useMemo(() => {
    const defs = [];
    const toKey = (href) => href === '/' ? 'dashboard.view' : href.replace(/^\//,'').replaceAll('/', '.') + '.view';
    for (const item of navigation) {
      if (item.type === 'single') {
        defs.push({ key: toKey(item.href || '/'), title: item.name, category: item.name });
      } else if (item.type === 'expandable') {
        for (const child of item.children || []) {
          defs.push({ key: toKey(child.href), title: child.name, category: item.name });
        }
      }
    }
    return defs;
  }, [navigation]);

  useEffect(() => {
    // sync only for admin
    if (user?.role !== 'admin') return;
    syncPermissions(permissionDefs).catch(() => {});
  }, [user, permissionDefs]);

  const [abilities, setAbilities] = useState(null);
  useEffect(() => {
    (async () => {
      try { setAbilities(await getMyAbilities()); } catch { setAbilities({}); }
    })();
  }, []);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const loadBranding = async () => {
      try {
        const response = await fetch('/api/company/public-branding', {
          signal: controller.signal
        });
        const payload = await response.json();

        if (!isMounted || !response.ok || !payload?.success || !payload?.data) {
          return;
        }

        const companyName = (payload.data.companyName || 'ERP').trim();
        const logoUrl = payload.data.logoUrl || '/api/company/logo';

        setBrandingName(companyName || 'ERP');
        setBrandingLogoUrl(logoUrl);
      } catch (error) {
        // Marka bilgisi alınamazsa varsayılan değerler korunur.
      }
    };

    loadBranding();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  const isActive = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  const toggleMenu = (key) => {
    setExpandedMenus(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const renderMenuItem = (item) => {
    const isAdminUser = user?.role === 'admin';
    const canSeeHref = (href) => {
      if (isAdminUser) return true;
      if (!abilities) return true;
      const key = pathToPermissionKey(href);
      return !!abilities[key];
    };
    const canSeeNavChild = (child) => {
      // Admin tüm menüleri görür (yeni eklenen izinler abilities cache'inde olmasa bile)
      if (isAdminUser) return true;
      if (child.adminOnly) return false;
      if (!abilities) return true;
      const key = pathToPermissionKey(child.href);
      return !!abilities[key];
    };
    if (item.type === 'expandable') {
      if (item.adminOnly && !isAdminUser) return null;
      const isExpanded = expandedMenus[item.key];
      const visibleChildren = (item.children || [])
        .filter((c) => canSeeNavChild(c));
      const hasActiveChild = visibleChildren.some(child => isActive(child.href));
      if (visibleChildren.length === 0) return null;
      
      return (
        <div key={item.name}>
          <button
            onClick={() => toggleMenu(item.key)}
            className={`
              w-full flex items-center py-2 text-sm font-medium rounded-md transition-colors
              ${isCollapsed ? 'justify-center px-2' : 'px-3'}
              ${hasActiveChild 
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' 
                : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
              }
            `}
            title={isCollapsed ? item.name : ''}
          >
            <div className="flex items-center">
              <item.icon className={`${isCollapsed ? 'w-6 h-6' : 'w-5 h-5'} flex-shrink-0`} />
              {!isCollapsed && <span className="ml-3">{item.name}</span>}
            </div>
            {!isCollapsed && (
              <ChevronDown 
                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
              />
            )}
          </button>
          
          {isExpanded && !isCollapsed && (
            <div className="ml-6 mt-1 space-y-1">
              {visibleChildren.map((child) => (
                  <NavLink
                    key={child.name}
                    to={child.href}
                    onClick={() => setOpen(false)}
                    className={`
                      block px-3 py-2 text-sm rounded-md transition-colors
                      ${isActive(child.href)
                        ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                        : 'text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-700'
                      }
                    `}
                  >
                    <span className="flex items-center space-x-2">
                      {child.icon && <child.icon className="w-4 h-4 flex-shrink-0" />}
                      <span>{child.name}</span>
                    </span>
                  </NavLink>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Single menu item
    if (item.adminOnly && !isAdminUser) return null;
    if (item.href && abilities && !canSeeHref(item.href)) return null;
    
    return (
      <NavLink
        key={item.name}
        to={item.href}
        onClick={() => setOpen(false)}
        className={`
          flex items-center py-2 text-sm font-medium rounded-md transition-colors
          ${isCollapsed ? 'justify-center px-2' : 'px-3'}
          ${isActive(item.href)
            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
            : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
          }
        `}
        title={isCollapsed ? item.name : ''}
      >
        <item.icon className={`${isCollapsed ? 'w-6 h-6' : 'w-5 h-5'} flex-shrink-0`} />
        {!isCollapsed && <span className="ml-3">{item.name}</span>}
      </NavLink>
    );
  };

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-40 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div 
        className={`
          fixed inset-y-0 left-0 z-50 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex flex-col
          ${open ? 'translate-x-0' : '-translate-x-full'}
          ${isCollapsed ? 'w-16 cursor-pointer' : 'w-64'}
        `}
        onClick={isCollapsed ? toggleSidebar : undefined}
      >
        {/* Header */}
        <div className={`flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 ${
          isCollapsed ? 'justify-center' : ''
        }`}>
          {!isCollapsed && (
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 flex items-center justify-center">
                <img 
                  src={brandingLogoUrl}
                  alt={`${brandingName} Logo`}
                  className="w-8 h-8 object-contain rounded-full"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center" style={{display: 'none'}}>
                  <span className="text-white text-sm font-bold">{brandingName.charAt(0).toUpperCase() || 'E'}</span>
                </div>
              </div>
              <span className="text-lg font-semibold text-gray-900 dark:text-white">
                {brandingName} ERP
              </span>
            </div>
          )}
          
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => {
                if (!isCollapsed) {
                  e.stopPropagation();
                }
                toggleSidebar();
              }}
              className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
              title={isCollapsed ? 'Genişlet' : 'Daralt'}
            >
              {isCollapsed ? (
                <ChevronRight className="w-5 h-5" />
              ) : (
                <ChevronLeft className="w-5 h-5" />
              )}
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
              className="lg:hidden p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Navigation - Scrollable */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden mt-6 px-3 pb-6">
          <div className="space-y-1">
            {navigation.map(renderMenuItem)}
          </div>
        </nav>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 p-3">
          <div className={`text-xs text-gray-500 dark:text-gray-400 ${isCollapsed ? 'text-center' : ''}`}>
            {isCollapsed && (
              <div className="text-center">
                <div className="font-bold text-gray-700 dark:text-gray-300">{brandingName.charAt(0).toUpperCase() || 'E'}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
